import listAlt, { buyCaptionFor, captionFor } from '@utils/alt/listAlt'
import offerLinkSlugified from '@utils/links/offerLinkSlugified'
import offerLinkBuySlugified from '@utils/links/offerLinkBuySlugified'
import capitalize from '@utils/string/capitalize'
import formatPrice from '@utils/price/format'
import formatPriceRange from '@utils/price/formatPriceRange'
import { netPriceToGross } from '@utils/offers/netPriceToGross'
import { shortTrimFor } from '@utils/offers/shortTrimFor'
import vehicleTitle from '@utils/offers/vehicleTitle'
import isFirm from '@utils/user/isFirm'
import { FUEL_CAPTIONS } from '@constants/fuels'
import { inscriptionLabel } from '@components/add/PhotoInscriptions'
import { offerSubtitle } from 'lib/offer/subtitle'

import { PROPER_NAME_DELIMITER, getBuySpec } from '../OfferShortSpec'
import { extractOfferData } from '../OfferListItemNewComponents'

const SPEC_MAX = 4

// Krótsze nazwy skrzyni, żeby parametry 2×2 mieściły się w karcie poziomej (jak w prototypie).
const GEARBOX_SHORT = {
    Automatyczna: 'Automat',
    Półautomatyczna: 'Półautomat',
    Bezstopniowa: 'CVT',
}

// Krótsze nazwy paliwa w karcie (pełna nazwa dla czytników ekranu).
const FUEL_SHORT = {
    'Hybryda Plug-in': 'Plug-in',
    Elektryczne: 'Elektryk',
    Elektryczny: 'Elektryk',
    'Benzyna+LPG': 'LPG',
}

// Sylwetka pod zdjęciem: osobowe według nadwozia, pozostałe pojazdy według kategorii.
const CAR_SILHOUETTES = {
    Coupe: 'coupe',
    Crossover: 'suv',
    Hatchback: 'hatch',
    Kabriolet: 'cabrio',
    Kombi: 'kombi',
    Minivan: 'minivan',
    Pickup: 'pickup',
    Sedan: 'sedan',
    SUV: 'suv',
}

export function silhouette (offer) {
    const type = offer?.type
    if (type === 'car_ad') return CAR_SILHOUETTES[offer.data?.body_type] || 'hatch'
    if (type === 'motorcycle_ad') return 'moto'
    if (type === 'ciezarowe') return 'truck'
    if (type === 'truck_ad') return offer.data?.truck_body_type === 'Pickup' ? 'pickup' : 'van'
    return null
}

// Parametry jak dziś na kartach (OfferFeatureGrid dla osobowych, getSpec dla pozostałych; bez pojemności),
// z etykietami dla czytników; maks. 4 pozycje, puste pominięte.
function spec (offer) {
    const items = []
    // short: krótsza forma do wyświetlenia, value zostaje pełna (dla czytników ekranu)
    const add = (label, value, short) => {
        const v = text(value)
        if (!v || items.length >= SPEC_MAX) return
        const s = text(short)
        items.push(s && s !== v ? { label, value: v, short: s } : { label, value: v })
    }
    const { data } = offer || {}
    const mileage = offer.mileage === 0 || offer.mileage ? `${formatPrice(Number(offer.mileage), ' ') || 0} km` : null

    add('Rok produkcji', offer.production_year)
    add('Przebieg', mileage)
    const fuel = FUEL_CAPTIONS[offer.fuel] || offer.fuel
    add('Paliwo', fuel, FUEL_SHORT[text(fuel)])
    add('Skrzynia biegów', data?.gearbox, GEARBOX_SHORT[text(data?.gearbox)])
    add('Motogodziny', offer.motohours && `${offer.motohours} h`)
    add('Kategoria', offer.category)
    add('Podkategoria', offer.subcategory)
    add('Rodzaj', offer.kind)
    for (const part of (offer.part_category ? offer.part_category.split(PROPER_NAME_DELIMITER) : [])) add('Kategoria części', part)
    add('Pojazd', offer.part_vehicle)
    add('Usługa', offer.service_kind)
    return items
}

// Parametry bez danych pojazdu (kategorie części, usług, narzędzi) w jednej kolumnie: dłuższe nazwy.
const hasVehicleSpec = (items) => items.some(i => ['Rok produkcji', 'Przebieg', 'Paliwo', 'Skrzynia biegów'].includes(i.label))

const text = (value) => (value == null ? '' : String(value).trim())

// Tytuł jak w podglądzie formularza: marka, model i wersja silnikowa (z niedozwoloną treścią — bez wersji);
// ogłoszenia z własnym tytułem (narzędzia, usługi) i części — bez zmian.
function title (offer) {
    if (offer.title || offer.part_name) return captionFor(offer)
    return vehicleTitle(offer.make, offer.model, shortTrimFor(offer)) || captionFor(offer)
}

// Linia pod tytułem: podtytuł ogłoszenia, bez niego — dane techniczne (stan „Nowy” na liście, nadwozie, moc).
function subline (offer, isNew = false) {
    const power = Number(offer.data?.engine_power)
    const body = capitalize(text(offer.data?.body_type || offer.data?.truck_body_type))
    return offerSubtitle(offer) || [isNew && 'Nowy', body, power > 0 && `${power} KM`].filter(Boolean).join(', ')
}

function price (offer) {
    const [wasNet, gross] = netPriceToGross(offer)
    const currency = offer.price?.currency || 'PLN'
    const unit = currency === 'PLN' ? 'zł' : currency
    // przy cenie: netto (wtedy faktura jest oczywista) albo „Faktura VAT”, oraz „Salon Polska”
    const notes = [
        wasNet ? `${formatPrice(offer.price.price, ' ')} ${unit} netto` : offer.price?.price_invoice && 'Faktura VAT',
        offer.data?.country_of_origin === 'Polska' && 'Salon Polska',
    ].filter(Boolean)
    // bez kwoty (0 albo brak): karta pokazuje „Cena do uzgodnienia”
    const value = formatPrice(gross, ' ') || ''
    return {
        value,
        unit,
        note: value ? notes.join(', ') : '',
    }
}

function photo (offer) {
    const { cloudflare, pid } = offer?.tracking || {}
    const { mainPhoto, photoCount, photos = [] } = cloudflare || {}
    if (mainPhoto || photos.length) return { cloudflareId: photos[0] || mainPhoto, count: photoCount ?? photos.length }
    if (pid) return { pid, count: 0 }
    return null
}

export function offerCardData (offer) {
    const { isNew, location, user } = extractOfferData({ offer })
    // typ sprzedawcy z konta, dla każdej kategorii
    const firm = isFirm(user)
    const [alt] = listAlt(offer)
    const specItems = spec(offer)
    return {
        alt,
        isNew,
        link: offerLinkSlugified(offer),
        location,
        photo: photo(offer),
        price: price(offer),
        seller: typeof user === 'object' && user ? (firm ? 'dealer' : 'private') : null,
        silhouette: silhouette(offer),
        spec: specItems,
        specWide: !hasVehicleSpec(specItems),
        title: title(offer),
        version: subline(offer),
    }
}

// Karta listy: pod tytułem podtytuł albo stan „Nowy”, nadwozie i moc; odznaki „Wyróżnione”, „Nowe” (48 h), rezerwacja
// i napis sprzedawcy; rata w notce przy cenie.
export function listingCardData (offer, { isFresh = false } = {}) {
    const card = offerCardData(offer)
    const { inscription, installment, isNew, isPart, reserved } = extractOfferData({ offer })
    const notes = [card.price.note, card.price.value && installment && `rata ${formatPrice(installment, ' ')} ${card.price.unit}/mies.`].filter(Boolean)
    const badges = [
        Boolean(offer.promotion?.search_list_top?.on) && { kind: 'promo', label: 'Wyróżnione' },
        isFresh && { kind: 'success', label: 'Nowe' },
        reserved && { kind: 'muted', label: 'Rezerwacja' },
        inscriptionLabel(inscription) && { kind: 'soft', label: inscriptionLabel(inscription) },
    ].filter(Boolean)

    return {
        ...card,
        badges,
        gallery: gallery(offer, card.photo),
        part: isPart ? part(offer) : null,
        price: { ...card.price, note: notes.join(', ') },
        version: subline(offer, isNew),
    }
}

// Przewijanie zdjęć w karcie: do 4 zdjęć (pierwsze to zdjęcie karty) i liczba wszystkich.
function gallery (offer, photo) {
    const photos = offer?.tracking?.cloudflare?.photos
    if (!photo?.cloudflareId || !Array.isArray(photos) || photos[0] !== photo.cloudflareId) return null
    const ids = photos.slice(0, 4)
    const total = Math.max(photo.count || 0, ids.length)
    return total > 1 ? { ids, more: total - ids.length } : null
}

// Część: ścieżka kategorii i pojazd w jednej linii, sposoby dostawy jako odznaki.
function part (offer) {
    const path = (offer.part_category ? offer.part_category.split(PROPER_NAME_DELIMITER) : []).map(text).filter(Boolean)
    return {
        category: [path.join(' › '), text(offer.part_vehicle)].filter(Boolean).join(' · '),
        delivery: [offer.data?.shipment && 'Wysyłka', offer.data?.personal_pickup && 'Odbiór osobisty'].filter(Boolean),
    }
}

// Ogłoszenie „kupię”: bez zdjęcia, zakres ceny, parametry jak na dotychczasowej liście.
export function buyCardData (offer) {
    const { location } = extractOfferData({ offer })
    const currency = offer.price?.currency || 'PLN'
    const power = Number(offer.data?.engine_power)
    return {
        link: offerLinkBuySlugified(offer),
        location,
        price: { value: offer.price?.od_price || offer.price?.do_price ? formatPriceRange(offer.price, ' ') : '', unit: currency === 'PLN' ? 'zł' : currency },
        spec: [power > 0 && `${power} KM`, ...getBuySpec(offer).filter(item => item !== '·')].filter(Boolean),
        title: buyCaptionFor(offer),
    }
}
